import React from 'react';
import type { PropertyPortalPublication } from 'src/api/generated';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import {
  canPauseMeliPublication,
  canPublishMeliPublication,
  canUnpublishMeliPublication,
} from '../../utils/meli-publication.util';

interface PropertyMeliPublishingManagerActionBarProps {
  currentPublication: PropertyPortalPublication | null;
  currentPublicationId: number | null;
  draftPublishBlockedByIssues: boolean;
  enqueueingPause: boolean;
  enqueueingPublish: boolean;
  enqueueingUnpublish: boolean;
  handleEnqueuePublish: () => Promise<void>;
  handlePersistedAction: (
    action: 'pause' | 'unpublish',
    publicationId: number
  ) => Promise<void>;
  isPropertyActive: boolean;
  isSyncMode: boolean;
  loadingAction: boolean;
}

export const PropertyMeliPublishingManagerActionBar: React.FC<
  PropertyMeliPublishingManagerActionBarProps
> = ({
  currentPublication,
  currentPublicationId,
  draftPublishBlockedByIssues,
  enqueueingPause,
  enqueueingPublish,
  enqueueingUnpublish,
  handleEnqueuePublish,
  handlePersistedAction,
  isPropertyActive,
  isSyncMode,
  loadingAction,
}) => {
  const { t } = useI18n();
  const canPauseCurrentPublication =
    currentPublicationId != null && canPauseMeliPublication(currentPublication);
  const canUnpublishCurrentPublication =
    currentPublicationId != null &&
    canUnpublishMeliPublication(currentPublication);
  const canPublishCurrentPublication =
    currentPublication == null || canPublishMeliPublication(currentPublication);

  return (
    <div className="mt-4 flex flex-wrap justify-end gap-3 border-t border-slate-200 pt-4">
      {canPauseCurrentPublication ? (
        <Button
          variant="warning"
          appearance="outline"
          onClick={() =>
            void handlePersistedAction('pause', currentPublicationId)
          }
          loading={enqueueingPause}
          disabled={loadingAction || isSyncMode}
        >
          {t('catalog.properties.meli.actions.pause')}
        </Button>
      ) : null}
      {canUnpublishCurrentPublication ? (
        <Button
          variant="danger"
          appearance="outline"
          onClick={() =>
            void handlePersistedAction('unpublish', currentPublicationId)
          }
          loading={enqueueingUnpublish}
          disabled={loadingAction || isSyncMode}
        >
          {t('catalog.properties.meli.actions.unpublish')}
        </Button>
      ) : null}
      {canPublishCurrentPublication ? (
        draftPublishBlockedByIssues ? (
          <Tooltip
            content={
              isPropertyActive !== true
                ? t(
                    'catalog.properties.editor.publishing.actions.publishInactiveTooltip'
                  )
                : t('catalog.properties.meli.alert.fixDraftIssues')
            }
            size="xs"
          >
            <span className="inline-flex">
              <Button
                variant="primary"
                onClick={() => void handleEnqueuePublish()}
                loading={enqueueingPublish}
                disabled
              >
                {t('catalog.properties.meli.actions.publish')}
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Button
            variant="primary"
            onClick={() => void handleEnqueuePublish()}
            loading={enqueueingPublish}
            disabled={loadingAction || isSyncMode}
          >
            {t('catalog.properties.meli.actions.publish')}
          </Button>
        )
      ) : null}
    </div>
  );
};
